import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Page, Section } from '@/components/ui/Page'
import { ErrorState, Loading, Notice } from '@/components/ui/States'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { ClientCard, DocumentTopBar, SaveState, projectLabel } from '@/features/documents/DocumentChrome'
import { LineItemsEditor, newLineKey, type QuickAdd } from '@/features/documents/LineItemsEditor'
import { planSteps, useBookFromQuote, useJobInvoices, usePaymentPlans, type DeliverableSeed } from '@/features/invoices/queries'
import { useSettings } from '@/features/settings/useSettings'
import { formatDateLong, shiftDays, today, type IsoDate } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { invoiceLinesFromQuote, planInstalments, type InstalmentPlan } from '@/lib/invoicing'
import { lineTotals, type EditableLine } from '@/lib/lineItems'
import { describeError } from '@/lib/supabase'
import {
  useAddons,
  useDeleteQuote,
  useGenerateQuotePdf,
  usePackages,
  usePricingRules,
  useQuote,
  useSaveQuoteDraft,
  useUpdateQuoteStatus,
  type QuoteWithLines,
} from './queries'

/**
 * A quote (29 Oct 2026), laid out like Pixieset's invoice: the client, the
 * items typed line by line (quantity × unit price), notes and how long it is
 * valid. A draft saves itself as she types. Sent, it is frozen: when the
 * couple says yes, Accept raises the invoice (and, on a project, books it).
 *
 * This replaces the July builder that priced from the rules engine; the
 * engine (lib/pricing.ts) still gives the travel rate for "Add travel".
 */
export function QuoteEditorPage() {
  const { quoteId = '' } = useParams()
  const query = useQuote(quoteId)
  if (query.isPending) return <Loading label="Opening the quote" />
  if (query.isError || !query.data)
    return (
      <Page>
        <ErrorState title="That quote could not open" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )
  return <Editor quote={query.data} />
}

const toEditable = (quote: QuoteWithLines): EditableLine[] =>
  quote.line_items.map((line) => ({
    key: newLineKey(),
    description: line.description,
    detail: line.detail ?? '',
    quantity: Number(line.quantity),
    unitPrice: Number(line.unit_price),
    source: line.source,
    sourceId: line.source_id,
  }))

function Editor({ quote }: { quote: QuoteWithLines }) {
  const navigate = useNavigate()
  const { staff } = useCurrentStaff()
  const settings = useSettings()
  const packages = usePackages()
  const addons = useAddons()
  const rules = usePricingRules((quote.job?.event_date ?? today()) as IsoDate)
  const save = useSaveQuoteDraft()
  const pdf = useGenerateQuotePdf()
  const setStatus = useUpdateQuoteStatus()
  const remove = useDeleteQuote()
  const book = useBookFromQuote()
  const invoices = useJobInvoices(quote.job_id ?? undefined)
  const plans = usePaymentPlans()
  const [planId, setPlanId] = useState<string | null>(null)

  const draft = quote.status === 'draft'
  const [lines, setLines] = useState<EditableLine[]>(() => toEditable(quote))
  const [validUntil, setValidUntil] = useState(quote.valid_until ?? '')
  const [coverNote, setCoverNote] = useState(quote.cover_note ?? '')
  const [touched, setTouched] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const vatRate = settings.data?.vat_registered ? Number(settings.data.vat_rate) : null
  const totals = lineTotals(lines, vatRate)

  // Save a moment after she stops typing.
  const latest = useRef({ lines, validUntil, coverNote, totals })
  latest.current = { lines, validUntil, coverNote, totals }
  useEffect(() => {
    if (!touched || !draft) return
    const timer = window.setTimeout(() => {
      const now = latest.current
      save.mutate(
        { quoteId: quote.id, lines: now.lines, totals: now.totals, validUntil: now.validUntil || null, coverNote: now.coverNote.trim() || null },
        { onSuccess: () => setSavedAt(new Date()) },
      )
    }, 900)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, validUntil, coverNote, touched, draft])

  const edit = <T,>(setter: (value: T) => void) => (value: T) => {
    setTouched(true)
    setter(value)
  }

  const quickAdds: QuickAdd[] = [
    ...(packages.data ?? []).map((pkg) => ({
      id: pkg.id,
      label: pkg.name,
      unitPrice: Number(pkg.base_price),
      detail: pkg.tagline ?? null,
      source: 'package' as const,
    })),
    ...(addons.data ?? []).map((addon) => ({
      id: addon.id,
      label: addon.name,
      unitPrice: Number(addon.unit_price),
      detail: `Per ${addon.unit_type}`,
      source: 'addon' as const,
    })),
  ]
  const rate = rules.data?.rules.travelRatePerKm ?? 0
  const km =
    quote.job?.distance_km === null || quote.job?.distance_km === undefined
      ? null
      : Math.round(Number(quote.job.distance_km) * (rules.data?.rules.travelChargesReturnTrip ? 2 : 1))

  // ── Accepting ───────────────────────────────────────────────────────────
  const planList = plans.data ?? []
  const chosenPlan = planList.find((plan) => plan.id === planId) ?? planList[0] ?? null
  const hasBookingInvoices = (invoices.data ?? []).some((invoice) => invoice.status !== 'void' && invoice.kind !== 'additional')

  function instalmentPlan(): InstalmentPlan | null {
    const total = Number(quote.total)
    if (total <= 0) return null
    // Without a project there is no wedding date for a plan to count from: one payment, due in 7 days.
    if (!quote.job) return { instalments: [{ label: 'Payment', amount: total, dueDate: shiftDays(today(), 7) }], warnings: [] }
    if (!chosenPlan) return null
    try {
      return planInstalments(planSteps(chosenPlan), total, today(), quote.job.event_date as IsoDate)
    } catch {
      return null
    }
  }

  function deliverablesFromQuote(): DeliverableSeed[] {
    const seeds: DeliverableSeed[] = []
    const add = (list: unknown) => {
      if (!Array.isArray(list)) return
      for (const item of list as Array<{ title?: unknown; kind?: unknown }>) {
        if (typeof item.title !== 'string') continue
        const kind = item.kind === 'film' || item.kind === 'album' || item.kind === 'other' ? item.kind : 'photo'
        if (!seeds.some((seed) => seed.title === item.title)) seeds.push({ title: item.title, kind })
      }
    }
    for (const line of quote.line_items) {
      if (line.source === 'package' && line.source_id) add(packages.data?.find((item) => item.id === line.source_id)?.deliverables)
      if (line.source === 'addon' && line.source_id) add(addons.data?.find((item) => item.id === line.source_id)?.deliverables)
    }
    return seeds
  }

  async function accept() {
    const plan = instalmentPlan()
    const s = settings.data
    if (!plan || !s) return
    for (const warning of plan.warnings) toast.warning(warning)
    const deliverables = quote.job ? deliverablesFromQuote() : []
    const eventDate = (quote.job?.event_date ?? today()) as IsoDate
    const weeksLater = (weeks: number) => shiftDays(eventDate, weeks * 7)
    await book.mutateAsync({
      jobId: quote.job_id,
      contactId: quote.contact_id,
      quoteId: quote.id,
      packageId: quote.package_id ?? quote.line_items.find((line) => line.source === 'package')?.source_id ?? null,
      invoice: {
        issueDate: today(),
        subtotal: Number(quote.subtotal),
        vat: Number(quote.vat),
        total: Number(quote.total),
        lines: invoiceLinesFromQuote(quote.line_items),
        instalments: plan.instalments,
        planName: quote.job ? (chosenPlan?.name ?? null) : null,
      },
      deliverables,
      photosDueDate: quote.job && (deliverables.length === 0 || deliverables.some((item) => item.kind === 'photo')) ? weeksLater(s.photo_delivery_weeks) : null,
      filmDueDate: quote.job && deliverables.some((item) => item.kind === 'film') ? weeksLater(s.film_delivery_weeks) : null,
      staffId: staff.id,
    })
    navigate(quote.job ? `/weddings/${quote.job.id}` : '/invoices')
  }

  async function openPdf() {
    const tab = window.open('', '_blank', 'noopener,noreferrer')
    try {
      // A draft is saved first, so the PDF shows what is on screen.
      if (draft && touched) {
        await save.mutateAsync({ quoteId: quote.id, lines, totals, validUntil: validUntil || null, coverNote: coverNote.trim() || null })
      }
      const url = await pdf.mutateAsync({ quoteId: quote.id, jobId: quote.job_id })
      if (tab) tab.location.href = url
      else window.location.href = url
    } catch {
      tab?.close()
    }
  }

  const preview = instalmentPlan()

  return (
    <Page className="max-w-[64rem]">
      <DocumentTopBar
        back="/quotes"
        title={`Quote ${quote.quote_number}`}
        status={<QuoteStatusBadge status={quote.status} />}
        project={projectLabel(quote.job)}
        projectTo={quote.job ? `/weddings/${quote.job.id}` : null}
        save={draft ? <SaveState saving={save.isPending} savedAt={savedAt} failed={save.isError} /> : null}
        actions={
          <>
            <Button loading={pdf.isPending} loadingLabel="Building" onClick={() => void openPdf()}>
              Open the PDF
            </Button>
            {draft ? (
              <Button
                variant="primary"
                disabled={lines.length === 0 || save.isPending}
                onClick={() =>
                  setStatus.mutate(
                    { id: quote.id, status: 'sent', jobId: quote.job_id },
                    { onSuccess: () => toast.success('Quote marked as sent') },
                  )
                }
              >
                Mark as sent
              </Button>
            ) : null}
          </>
        }
      />

      {draft ? (
        <Notice className="mb-4" tone="amber" title="Draft">
          Open the PDF and send it to them, then mark it as sent. Once sent, the quote is frozen.
        </Notice>
      ) : (
        <AcceptPanel
          status={quote.status}
          withProject={Boolean(quote.job)}
          hasBookingInvoices={hasBookingInvoices}
          plans={planList.map((plan) => ({ id: plan.id, name: plan.name }))}
          planId={chosenPlan?.id ?? null}
          onPlanChange={setPlanId}
          preview={preview}
          busy={setStatus.isPending || book.isPending}
          onAccept={() => void accept()}
          onDecline={() =>
            setStatus.mutate({ id: quote.id, status: 'declined', jobId: quote.job_id }, { onSuccess: () => toast.success('Quote marked as declined') })
          }
        />
      )}

      <Section>
        <ClientCard client={quote.contact} />
      </Section>

      <Section title="Items">
        <LineItemsEditor
          lines={lines}
          onChange={edit(setLines)}
          readOnly={!draft}
          vatRate={vatRate}
          quickAdds={quickAdds}
          travel={{ ratePerKm: rate, km }}
        />
      </Section>

      <Section title="Notes">
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_14rem]">
          <Field label="Note at the top of the quote" hint="In your words. Left blank, the quote goes out with none.">
            <Textarea rows={4} disabled={!draft} value={coverNote} onChange={(e) => edit(setCoverNote)(e.target.value)} />
          </Field>
          <Field label="Valid until" hint="After this date it shows as expired.">
            <Input type="date" disabled={!draft} value={validUntil} onChange={(e) => edit(setValidUntil)(e.target.value)} />
          </Field>
        </div>
      </Section>

      {draft ? (
        <div className="mt-2">
          <Button
            size="sm"
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              if (!window.confirm(`Delete quote ${quote.quote_number}? It has never been sent.`)) return
              remove.mutate({ id: quote.id, jobId: quote.job_id }, { onSuccess: () => navigate('/quotes') })
            }}
          >
            Delete this draft
          </Button>
        </div>
      ) : null}
    </Page>
  )
}

function AcceptPanel({
  status,
  withProject,
  hasBookingInvoices,
  plans,
  planId,
  onPlanChange,
  preview,
  busy,
  onAccept,
  onDecline,
}: {
  status: string
  withProject: boolean
  hasBookingInvoices: boolean
  plans: Array<{ id: string; name: string }>
  planId: string | null
  onPlanChange: (id: string) => void
  preview: InstalmentPlan | null
  busy: boolean
  onAccept: () => void
  onDecline: () => void
}) {
  if (status === 'declined')
    return (
      <Notice className="mb-4" tone="clay" title="Declined">
        This quote is frozen. Make a new quote to offer something different.
      </Notice>
    )
  const invoiced = status === 'accepted' && (hasBookingInvoices || !withProject)
  if (invoiced)
    return (
      <Notice className="mb-4" tone="sage" title="Accepted">
        {withProject ? 'Booked. The invoice and its payment plan are on the project.' : 'Accepted. The invoice is under Invoices.'}
      </Notice>
    )
  return (
    <Notice className="mb-4" tone={status === 'accepted' ? 'sage' : 'amber'} title={status === 'accepted' ? 'Accepted' : 'Sent: frozen'}>
      <span className="flex flex-col gap-3">
        <span>
          {withProject
            ? 'When they say yes, choose the payment plan and accept: the invoice is raised, the deliverables and delivery dates are filled in, and the project is marked as booked.'
            : 'When they say yes, accept: the invoice is raised with one payment, due in 7 days. You can change it on the invoice before sending.'}
        </span>
        {withProject ? (
          <select
            aria-label="Payment plan"
            value={planId ?? ''}
            onChange={(event) => onPlanChange(event.target.value)}
            className="h-[var(--size-control)] max-w-md rounded-md border border-[var(--color-rule-strong)] bg-transparent px-2 text-[length:var(--text-sm)]"
          >
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        ) : null}
        {preview ? (
          <span className="flex flex-col text-[length:var(--text-2xs)] text-taupe">
            {preview.instalments.map((item) => (
              <span key={item.label} data-numeric>
                {item.label}: {formatMoney(item.amount)}, due {formatDateLong(item.dueDate)}
              </span>
            ))}
          </span>
        ) : null}
        <span className="flex gap-2">
          <Button size="sm" variant="primary" disabled={busy || !preview} onClick={onAccept}>
            {status === 'accepted' ? 'Raise the invoice' : 'Accept and raise invoice'}
          </Button>
          {status !== 'accepted' ? (
            <Button size="sm" variant="ghost" disabled={busy} onClick={onDecline}>
              Declined
            </Button>
          ) : null}
        </span>
      </span>
    </Notice>
  )
}

export function QuoteStatusBadge({ status }: { status: string }) {
  const tone = status === 'accepted' ? 'sage' : status === 'declined' || status === 'expired' ? 'clay' : status === 'draft' ? 'neutral' : 'amber'
  return <Badge tone={tone}>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>
}

